// Styles are data: a fixed list of properties, each with the values it may
// take, written into one constructed stylesheet keyed by node id. Nothing
// here can load or run anything, and nothing can lift a node out of the
// page over the editor's own controls.
// meets: style/allowlist, style/tokens, style/live

const num = String.raw`-?(?:\d+\.?\d*|\.\d+)`;
const token = String.raw`var\(--[a-z][a-z0-9-]*\)`;
const length = new RegExp(String.raw`^(?:${num}(?:px|rem|em|%|ch|vh|vw)?|${token})$`);
const color = new RegExp(String.raw`^(?:#[0-9a-f]{3,8}|(?:rgb|hsl|oklch|oklab)a?\([0-9a-z.,%\s/+-]{1,60}\)|[a-z]{3,20}|${token})$`, "i");
const words = (...w: string[]) => new RegExp(`^(?:${w.join("|")}|${token})$`);
const box = (v: string) => v.split(/\s+/).length <= 4 && v.split(/\s+/).every((p) => length.test(p) || p === "auto");
const number = new RegExp(`^(?:${num}|${token})$`);

type Check = RegExp | ((v: string) => boolean);
export const PROPERTIES: Record<string, { check: Check; group: string }> = {};
const add = (group: string, check: Check, ...props: string[]) => props.forEach((p) => (PROPERTIES[p] = { check, group }));

add("Colour", color, "color", "background-color", "border-color");
add("Type", length, "font-size", "letter-spacing");
add("Type", words("normal", "bold", "[1-9]00"), "font-weight");
add("Type", words("normal", "italic"), "font-style");
add("Type", words("left", "center", "right", "justify", "start", "end"), "text-align");
add("Type", (v) => number.test(v) || length.test(v), "line-height");
add("Space", box, "padding", "margin");
add("Space", (v) => length.test(v) || v === "auto", "padding-top", "padding-right", "padding-bottom", "padding-left",
  "margin-top", "margin-right", "margin-bottom", "margin-left", "gap");
add("Box", (v) => length.test(v) || v === "auto", "width", "max-width", "min-height");
add("Box", length, "border-width", "border-radius");
add("Box", words("none", "solid", "dashed", "dotted"), "border-style");
add("Box", (v) => number.test(v) && (v.startsWith("var(") || (+v >= 0 && +v <= 1)), "opacity");
add("Layout", words("block", "flex", "grid", "inline-block", "none"), "display");
add("Layout", words("row", "column", "row-reverse", "column-reverse"), "flex-direction");
add("Layout", words("start", "end", "center", "stretch", "space-between", "space-around", "flex-start", "flex-end"),
  "justify-content", "align-items");
add("Layout", words("wrap", "nowrap"), "flex-wrap");

export function allowStyle(prop: string, value: unknown): value is string {
  const p = PROPERTIES[prop];
  if (!p || typeof value !== "string" || value.length > 80) return false;
  const v = value.trim();
  if (/[;{}<>\\@"'`!]|url\s*\(|expression|javascript:/i.test(v)) return false;
  return typeof p.check === "function" ? p.check(v) : p.check.test(v);
}

// The page's default theme. A page's own tokens override these.
export const TOKENS: Record<string, string> = {
  "--accent": "oklch(55% 0.17 255)",
  "--ink": "light-dark(oklch(22% 0.015 260), oklch(94% 0.006 85))",
  "--muted": "light-dark(oklch(47% 0.015 260), oklch(72% 0.01 85))",
  "--surface": "light-dark(oklch(96% 0.008 85), oklch(21% 0.01 260))",
  "--warn": "oklch(70% 0.15 70)",
  "--line": "light-dark(oklch(91% 0.006 260), oklch(30% 0.01 260))",
  "--space-1": "4px",
  "--space-2": "8px",
  "--space-3": "16px",
  "--space-4": "32px",
  "--radius": "8px",
};

export type Rule = { id: string; style: Record<string, string> };

// The whole page's styles as one stylesheet's text. Every value is checked
// again here, since a merge can bring a value no single edit was allowed.
export function css(rules: Rule[], tokens: Record<string, string>, scope = ".page"): string {
  // The defaults are ours and written as is; a page's own are checked.
  const vars = Object.entries(TOKENS).map(([k, v]) => `${k}:${v};`).join("") + Object.entries(tokens)
    .filter(([k, v]) => /^--[a-z][a-z0-9-]*$/.test(k) && (allowStyle("color", v) || allowStyle("padding", v)))
    .map(([k, v]) => `${k}:${v};`)
    .join("");
  const out = [`${scope}{${vars}}`];
  for (const r of rules) {
    const decls = Object.entries(r.style)
      .filter(([k, v]) => allowStyle(k, v))
      .map(([k, v]) => `${k}:${v.trim()};`)
      .join("");
    if (decls && /^[0-9]+@[0-9]+$/.test(r.id)) out.push(`${scope} [data-node="${r.id}"]{${decls}}`);
  }
  return out.join("\n");
}
